<template>
  <div class="course-comment">
    <div class="comment-header">
      <h3>课程评论</h3>
      <span class="comment-count">({{totalCount}}条评论)</span>
    </div>

    <!-- 评论输入区域 - 仅登录用户显示 -->
    <div class="comment-input-wrap" v-if="isLoggedIn && !isBlacklisted">
      <div class="comment-input">
        <textarea
          v-model="commentContent"
          :maxlength="maxLength"
          :placeholder="placeholder"
          class="comment-textarea"
          @input="handleInput"
        ></textarea>
        <div class="input-footer">
          <span class="char-count">{{remainingChars}} / {{maxLength}}</span>
          <t-button theme="primary" size="small" :loading="submitting" @click="handleSubmit">提交</t-button>
        </div>
      </div>
    </div>

    <!-- 拉黑用户提示 -->
    <div class="blacklist-tip" v-if="isBlacklisted">
      <t-icon name="info-circle" />
      <span>您已被限制评论</span>
    </div>

    <!-- 游客提示 -->
    <div class="login-tip" v-if="!isLoggedIn">
      <span>登录后可参与评论</span>
    </div>

    <!-- 评论列表 -->
    <div class="comment-list">
      <div v-if="loading" class="loading-state">
        <span>加载中...</span>
      </div>
      <div v-else-if="comments.length === 0" class="empty-state">
        <span>暂无评论，快来抢沙发~</span>
      </div>
      <template v-else>
        <div
          v-for="comment in comments"
          :key="comment.commentId"
        >
          <CommentItem
            :comment="comment"
            :course-id="courseId"
            :is-logged-in="isLoggedIn"
            :is-blacklisted="isBlacklisted"
            @reply-submitted="handleReplySubmitted"
            @replies-updated="handleRepliesUpdated"
          />
        </div>

        <!-- 加载更多 -->
        <div
          class="load-more"
          v-if="hasMore"
          @click="loadMore"
        >
          <span>加载更多</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useStore, mapState } from 'vuex'
import { MessagePlugin } from 'tdesign-vue-next'
import CommentItem from './CommentItem.vue'

export default {
  name: 'CourseComment',
  components: { CommentItem },
  props: {
    courseId: {
      type: [Number, String],
      required: true
    },
    isLoggedIn: {
      type: Boolean,
      default: false
    },
    isBlacklisted: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState(['userInfo'])
  },
  setup(props) {
    const comments = ref([])
    const totalCount = ref(0)
    const loading = ref(false)
    const submitting = ref(false)
    const commentContent = ref('')
    const pageNum = ref(1)
    const pageSize = ref(20)
    const hasMore = computed(() => comments.value.length < totalCount.value)

    const maxLength = 500
    const placeholder = '若有疑问可在此输入你的问题，官方老师将对你的问题进行解答'
    const defaultAvatar = '//cdn.vip.com/vshop/logo/img/avatar-default.png'

    const remainingChars = computed(() => maxLength - commentContent.value.length)

    // 校验评论内容：禁止表情符号和尖括号
    const validateContent = (content) => {
      if (!content) return { valid: true }

      // 检测 emoji 表情符号
      const emojiRegex = /[\u{1F000}-\u{1FFFF}]/u
      if (emojiRegex.test(content)) {
        return { valid: false, message: '评论内容不能包含表情符号' }
      }

      // 检测尖括号（防止XSS和数据库报错）
      if (/[<>]/.test(content)) {
        return { valid: false, message: '评论内容不能包含尖括号 < >' }
      }

      return { valid: true }
    }

    const store = useStore()

    const fetchComments = async (reset = false) => {
      if (reset) {
        pageNum.value = 1
        comments.value = []
      }
      loading.value = true
      try {
        const res = await store.dispatch('comment/queryCommentList', {
          courseId: props.courseId,
          pageNum: pageNum.value,
          pageSize: pageSize.value
        })
        console.log(22, res)
        if (res) {
          if (reset) {
            comments.value = res.list || []
          } else {
            comments.value.push(...(res.list || []))
          }
          totalCount.value = res.total || 0
        }
      } catch (error) {
        console.error('获取评论列表失败', error)
      } finally {
        loading.value = false
      }
    }

    const handleInput = () => {}

    const handleSubmit = async () => {
      const content = commentContent.value.trim()
      if (!content) return

      // 前端校验：禁止表情和尖括号
      const validation = validateContent(content)
      if (!validation.valid) {
        MessagePlugin.warning(validation.message)
        return
      }

      submitting.value = true
      try {
        await store.dispatch('comment/submitComment', {
          courseId: props.courseId,
          content
        })
        commentContent.value = ''
        fetchComments(true)
      } catch (error) {
        console.error('评论提交失败', error)
      } finally {
        submitting.value = false
      }
    }

    const loadMore = () => {
      pageNum.value++
      fetchComments(false)
    }

    // 处理子评论提交后的刷新
    const handleReplySubmitted = async () => {
      // 重新获取评论列表以更新嵌套结构
      await fetchComments(true)
    }

    // 处理子评论列表更新
    const handleRepliesUpdated = ({ commentId, replies, append }) => {
      const updateReplies = (items) => {
        for (const item of items) {
          if (item.commentId === commentId) {
            if (append) {
              // 追加模式：合并现有回复和新回复
              const existingIds = new Set(item.replies.map(r => r.commentId))
              const newReplies = replies.filter(r => !existingIds.has(r.commentId))
              item.replies = [...item.replies, ...newReplies]
            } else {
              item.replies = replies
            }
            return true
          }
          if (item.replies && updateReplies(item.replies)) {
            return true
          }
        }
        return false
      }
      updateReplies(comments.value)
    }

    watch(() => props.courseId, (newVal) => {
      if (newVal) {
        fetchComments(true)
      }
    }, { immediate: true })

    return {
      comments,
      totalCount,
      loading,
      submitting,
      commentContent,
      hasMore,
      maxLength,
      placeholder,
      defaultAvatar,
      remainingChars,
      handleInput,
      handleSubmit,
      loadMore,
      handleReplySubmitted,
      handleRepliesUpdated
    }
  }
}
</script>

<style lang="scss" scoped>
.course-comment {
  padding: 20px 0;
  border-top: 1px solid #E7E7E7;
  margin-top: 20px;

  .comment-header {
    display: flex;
    align-items: center;
    margin-bottom: 16px;

    h3 {
      font-size: 18px;
      font-weight: 600;
      color: #333;
      margin: 0;
    }

    .comment-count {
      margin-left: 8px;
      font-size: 14px;
      color: #999;
    }
  }

  .comment-input-wrap {
    margin-bottom: 20px;

    .comment-input {
      border: 1px solid #E7E7E7;
      border-radius: 8px;
      overflow: hidden;

      &:focus-within {
        border-color: #FD3374;
      }

      .comment-textarea {
        width: 100%;
        min-height: 100px;
        padding: 12px;
        border: none;
        resize: vertical;
        font-size: 14px;
        font-family: inherit;
        box-sizing: border-box;

        &:focus {
          outline: none;
        }

        &.small {
          min-height: 60px;
        }
      }

      .input-footer {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 12px;
        background: #F5F5F5;
        border-top: 1px solid #E7E7E7;

        .char-count {
          font-size: 12px;
          color: #999;
        }
      }
    }
  }

  .blacklist-tip,
  .login-tip {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: #FFF5F5;
    border: 1px solid #FFE0E0;
    border-radius: 8px;
    margin-bottom: 20px;
    font-size: 14px;
    color: #FF6B6B;
  }

  .comment-list {
    .loading-state,
    .empty-state,
    .load-more {
      text-align: center;
      padding: 20px;
      color: #999;
      font-size: 14px;
    }

    .load-more {
      cursor: pointer;
      color: #157DF9;
      &:hover {
        text-decoration: underline;
      }
    }

    .comment-item {
      padding: 8px 0 0;
      border-bottom: 1px solid #F0F0F0;

      &:last-child {
        border-bottom: none;
      }

      .comment-main {
        display: flex;
        gap: 12px;

        .comment-avatar {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          overflow: hidden;
          flex-shrink: 0;

          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
        }

        .comment-content {
          flex: 1;

          .comment-meta {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 8px;

            .comment-author {
              font-size: 14px;
              font-weight: 500;
              color: #333;
            }

            .official-tag {
              padding: 2px 6px;
              background: #FD3374;
              color: #fff;
              font-size: 12px;
              border-radius: 4px;
            }

            .comment-time {
              font-size: 12px;
              color: #999;
            }
          }

          .comment-text {
            font-size: 14px;
            color: #333;
            line-height: 1.6;
            word-break: break-word;
          }

          .comment-actions {
            margin-top: 8px;

            .action-btn {
              font-size: 12px;
              color: #157DF9;
              cursor: pointer;
              &:hover {
                text-decoration: underline;
              }
            }
          }
        }
      }

      .reply-input-wrap {
        margin-top: 12px;
        margin-left: 52px;

        .comment-textarea {
          border: 1px solid #E7E7E7;
          border-radius: 8px;
          padding: 8px 12px;
          width: 100%;
          font-size: 14px;
          resize: none;
          &:focus {
            outline: none;
            border-color: #FD3374;
          }
        }

        .input-footer {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 8px;
          margin-top: 8px;

          .char-count {
            margin-right: auto;
          }
        }
      }

      .comment-replies {
        margin-top: 12px;
        margin-left: 52px;
        padding-left: 16px;
        border-left: 2px solid #F0F0F0;

        .reply-item {
          display: flex;
          gap: 8px;
          padding: 12px 0;

          .reply-avatar {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            overflow: hidden;
            flex-shrink: 0;

            img {
              width: 100%;
              height: 100%;
              object-fit: cover;
            }
          }

          .reply-content {
            flex: 1;

            .comment-meta {
              display: flex;
              align-items: center;
              gap: 8px;
              margin-bottom: 4px;

              .comment-author {
                font-size: 13px;
                font-weight: 500;
                color: #333;
              }

              .official-tag {
                padding: 1px 4px;
                background: #FD3374;
                color: #fff;
                font-size: 10px;
                border-radius: 2px;
              }

              .comment-time {
                font-size: 11px;
                color: #999;
              }
            }

            .comment-text {
              font-size: 13px;
              color: #333;
              line-height: 1.5;
            }
          }
        }

        .load-more-replies {
          padding: 8px 0;
          font-size: 12px;
          color: #157DF9;
          cursor: pointer;
          &:hover {
            text-decoration: underline;
          }
        }
      }
    }
  }
}
</style>
